/**
 * @flow
 */

import React from 'react';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { Form } from 'semantic-ui-react';
import { FormattedMessage } from 'react-intl';
import {
    get,
    find,
    filter,
    map,
    uniqBy,
    flatten,
    property,
    reduce,
} from 'lodash';

import InvoiceFields from './InvoiceFields';
import { MASTER_INVOICE_FIELDS_FRAGMENT } from '../../../containers/masterAccessQueries';

import type { Invoice } from '../../../../../type';

export const GET_MASTERS_QUERY = gql`
    query getMasters($salon: ID!, $lang: String!, $services: [String]) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    masters(deleted: true) {
                        nodes {
                            id
                            firstName
                            lastName
                            deletedAt
                            serviceGroups(services: $services) {
                                id
                                name
                                services {
                                    id
                                    name(lang: $lang)
                                    duration
                                    price
                                }
                            }
                        }
                    }
                    cabinets(deleted: true) {
                        id
                        name(lang: $lang)
                        serviceGroups(services: $services) {
                            id
                            name
                            services {
                                id
                                name(lang: $lang)
                                duration
                                price
                            }
                        }
                    }
                }
            }
            ...onMasterInvoiceFields
        }
    }

    ${MASTER_INVOICE_FIELDS_FRAGMENT}
`;

type InvoiceFieldsListProps = {
    fields: Array<Object>,
    append: Function,
    remove: Function,
    readOnly: boolean,
    paid: boolean,
    invoice: Invoice,
    membership: ?Object,
};

const InvoiceFieldsList = ({
    fields,
    append,
    remove,
    readOnly,
    paid,
    invoice,
    membership,
}: InvoiceFieldsListProps) => {
    const salon = useSelector(({ user }) => user.get('salon'));
    const currentLanguage = useSelector(
        ({ intl: i }) => i.get('locale') || i.get('defaultLanguage'),
    );
    const isMaster = useSelector(({ user }) => user.get('isMaster'));

    const items = invoice ? invoice.items : [];

    const serviceIds = items
        ? map(filter(items, item => item.type === 'SERVICE'), i => i.entity.id)
        : [];

    const { data, loading } = useQuery(GET_MASTERS_QUERY, {
        variables: { salon, lang: currentLanguage, services: serviceIds },
        fetchPolicy: 'cache-and-network',
        skip: !salon,
    });

    let masters = [];
    let cabinets = [];
    let allServices = [];

    if (!loading && data && data.viewer) {
        const { viewer } = data;

        masters = filter(
            get(viewer, 'salon.masters.nodes'),
            ({ deletedAt }) => (!paid ? !deletedAt : true),
        );

        cabinets = filter(get(viewer, 'salon.cabinets'), ({ deletedAt }) =>
            !paid ? !deletedAt : true,
        );

        const mastersServiceGroups = masters
            ? uniqBy(
                  flatten(map(masters, i => i.serviceGroups)),
                  property('id'),
              )
            : [];

        const mastersServices =
            mastersServiceGroups && mastersServiceGroups.length
                ? reduce(
                      mastersServiceGroups,
                      (res, { services }) => [...res, ...services],
                      [],
                  )
                : [];

        const cabinetServiceGroups = cabinets
            ? uniqBy(
                  flatten(map(cabinets, i => i.serviceGroups)),
                  property('id'),
              )
            : [];

        const cabinetsServices =
            cabinetServiceGroups && cabinetServiceGroups.length
                ? reduce(
                      cabinetServiceGroups,
                      (res, { services }) => [...res, ...services],
                      [],
                  )
                : [];

        allServices = [...mastersServices, ...cabinetsServices];
    }

    return (
        <div>
            {fields.map((fieldItem, idx) => (
                <Form.Field key={fieldItem.id}>
                    <InvoiceFields
                        onRemove={() => remove(idx)}
                        index={idx}
                        readOnly={
                            readOnly ||
                            !!get(items, [idx, 'appointment', 'id'])
                        }
                        paid={paid}
                        masters={masters}
                        cabinets={cabinets}
                        sellerType={get(items, [idx, 'sellerType'])}
                        membership={membership}
                        invoice={items}
                        paidWithMembership={find(
                            items,
                            i => i.paymentType === 'MEMBERSHIP',
                        )}
                        allServices={allServices}
                    />
                </Form.Field>
            ))}
            {!readOnly && !isMaster && (
                <a
                    href="#add"
                    onClick={e => {
                        e.preventDefault();
                        append({});
                    }}
                >
                    +{' '}
                    <FormattedMessage
                        id="pages.appointments.details.invoice.InvoiceForm.add_item"
                        defaultMessage="Add item"
                    />
                </a>
            )}
        </div>
    );
};

export default InvoiceFieldsList;
